/// FOURJS_START_COPYRIGHT(D,2019)
/// Property of Four Js*
/// (c) Copyright Four Js 2019, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("RowBoundDecoratorWidget", ["WidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * RowBound decorator.
   * @class RowBoundDecoratorWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   * @publicdoc
   */
  cls.RowBoundDecoratorWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.RowBoundDecoratorWidget.prototype */ {
      __name: "RowBoundDecoratorWidget",

      /** @type {classes.ContextMenuWidget} */
      _contextMenuWidget: null,

      /** @type {Element} */
      _contextMenuIconElement: null,
      /** @type {number|null} */
      _order: null,
      /** @type {HandleRegistration|null} */
      _onMenuOpenHandler: null,
      /** @type {HandleRegistration|null} */
      _onMenuCloseHandler: null,
      /** @type {HandleRegistration|null} */
      _onActionChangeHandler: null,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        this._ignoreLayout = true;
        $super._initElement.call(this);

        this._contextMenuIconElement = this._element.getElementsByTagName("i")[0];
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._contextMenuIconElement = null;
        this._contextMenuWidget = null;
        if (this._onActionChangeHandler) {
          this._onActionChangeHandler();
          this._onActionChangeHandler = null;
        }
        if (this._onMenuOpenHandler) {
          this._onMenuOpenHandler();
          this._onMenuOpenHandler = null;
        }
        if (this._onMenuCloseHandler) {
          this._onMenuCloseHandler();
          this._onMenuCloseHandler = null;
        }
        $super.destroy.call(this);
      },

      /**
       * Returns rowbound container widget
       * @returns {classes.TableWidgetBase|classes.StretchableScrollGridWidget} rowBound container widget
       */
      getRowBoundContainerWidget: function () {
        let rowBoundContainerWidget = this.getTableWidgetBase();
        if (!rowBoundContainerWidget && this.getParentWidget().isInstanceOf(cls.StretchableScrollGridLineWidget)) {
          rowBoundContainerWidget = this.getParentWidget().getParentWidget();
        }
        return rowBoundContainerWidget;
      },

      /**
       * Displays the row bound menu
       *
       */
      openRowBoundMenu: function () {
        const rowBoundContainerWidget = this.getRowBoundContainerWidget();
        if (!rowBoundContainerWidget) {
          return true;
        }

        // request open context  menu
        const rowBoundWidget = rowBoundContainerWidget.getRowBoundMenu();
        if (!rowBoundWidget) {
          return true;
        }
        rowBoundWidget.parentElement = this._contextMenuIconElement;
        rowBoundWidget.reverseX = true;
        rowBoundContainerWidget.getRowBoundMenu().onClose(this.hide);
        this._onMenuOpenHandler = rowBoundContainerWidget
          .getRowBoundMenu()
          .when(context.constants.widgetEvents.dropDownOpen, this._showOnMenuOpen.bind(this));
        this._onMenuCloseHandler = rowBoundContainerWidget
          .getRowBoundMenu()
          .when(context.constants.widgetEvents.dropDownClose, this._hideOnMenuClose.bind(this));

        this.setAriaAttribute("controls", rowBoundWidget.getRootClassName());
        rowBoundWidget.show();
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (domEvent.target === null) {
          return true;
        } // just ignore qa click

        if (!this.isEnabled()) {
          return $super.manageMouseClick.call(this, domEvent);
        }

        const rowBoundContainerWidget = this.getRowBoundContainerWidget();
        if (!rowBoundContainerWidget) {
          return true;
        }

        // request focus
        if (this.getParentWidget().requestFocus) {
          this.getParentWidget().requestFocus(domEvent);
        }

        if (cls.DOMElementHelper.isElementOrChildOf(domEvent.target, this._contextMenuIconElement)) {
          const rowBoundWidget = rowBoundContainerWidget.getRowBoundMenu();
          if (rowBoundWidget) {
            rowBoundContainerWidget.emit(context.constants.widgetEvents.rowBoundMenu);
            return false;
          }
        }
        return true;
      },

      /**
       * Keyboard activation of the focused decorator (accessibility mode
       * routes keys to the DOM-focused widget before dialog accelerators).
       * No activeElement check needed: the widget exposes a single focusable
       * element, so being routed here implies it holds the DOM focus.
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this._isActivationKey(keyString, repeat) && this._isDOMTabbable()) {
          this.getRowBoundContainerWidget()?.emit(context.constants.widgetEvents.rowBoundMenu);
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Set the reference to rowBound context menu
       * @param {classes.ContextMenuWidget} contextMenu
       */
      setContextMenuWidget: function (contextMenu) {
        if (this._onActionChangeHandler) {
          this._onActionChangeHandler();
          this._onActionChangeHandler = null;
        }
        this._contextMenuWidget = contextMenu;
        if (this._contextMenuWidget) {
          this._onActionChangeHandler = this._contextMenuWidget.when("onActionChange", this.update.bind(this));
        }
      },

      /**
       * Sets index order of row bound cell
       * @param {number} colIndex - order index
       */
      setOrder: function (colIndex) {
        const rowWidget = this.getParentWidget();
        // take row index, start index with 1 (instead of 0) and generate a 4 digits number
        const rowIndex = (rowWidget.getRowIndex() + 1) * rowWidget.getOrderMultiplier();
        // add column index to row index
        let order = rowIndex + colIndex;
        order = order * 10;
        this._order = order;
        this.getElement().style.order = order.toString();
      },

      /**
       * @inheritDoc
       */
      getDOMTabIndexElements: function () {
        return [this._element];
      },

      /**
       * Actionable only when its container (table / scrollgrid) is enabled
       * and flags VM row actions, the decorator itself is enabled
       * (header/footer ones never are) and the rowbound menu has at least
       * one action.
       * @inheritDoc
       */
      _isDOMTabbable: function () {
        if (!this.isEnabled() || !this._contextMenuWidget || this._contextMenuWidget.getActionWidgets().size === 0) {
          return false;
        }
        const container = this.getRowBoundContainerWidget();
        return Boolean(container?.isEnabled() && container.haveRowBoundActions());
      },

      /**
       * A non-actionable decorator is a ghost button (icon hidden, no action):
       * `inert` drops it from the a11y tree and from mouse interaction, while
       * the root stays visible so TestCafe visibility counting is unaffected.
       * @inheritDoc
       */
      _updateDOMTabIndexFromState: function () {
        if (!this._element) {
          return;
        }
        $super._updateDOMTabIndexFromState.call(this);
        const tabbable = this._isDOMTabbable();
        // `inert` blurs a focused element to <body>: hand the focus to the
        // container root first so the Tab position survives losing the actions
        if (!tabbable && this._element.contains(document.activeElement)) {
          this.getRowBoundContainerWidget()?.getElement().focus();
        }
        this._element.toggleAttribute("inert", !tabbable);
      },

      /**
       * Update (visibility & quick actions)
       */
      update: function () {
        if (this._contextMenuWidget) {
          const actions = this._contextMenuWidget.getActionWidgets();
          cls.DOMElementHelper.toggleClass(this._contextMenuIconElement, "hidden", actions.size === 0);
        }
        this._updateDOMTabIndexFromState();
      },

      /**
       * Blur data on item widget
       * @param enable
       */
      blur: function (enable) {
        if (enable === this._isBlurred) {
          return;
        }
        this._isBlurred = enable;
        this.toggleClass("blurred", this._isBlurred);
      },

      /**
       * This method is meant to be called by the rowbound menu dropdown events
       * It unbind the show handler to avoid showing the rowbound everytime the dropdown open
       * then show the rowBound via CSS variable attribution
       * @private
       */
      _showOnMenuOpen: function () {
        if (this._onMenuOpenHandler) {
          this._onMenuOpenHandler();
          this._onMenuOpenHandler = null;
        }
        this.setStyle({
          "--rowBoundVisibility": "visible"
        });
        this.setAriaAttribute("expanded", true);
      },

      /**
       * This method is meant to be called by the rowbound menu dropdown events
       * It unbind the hide handler to avoid being called everytime the dropdown close
       * then hide the rowBound by unsetting the CSS variable responsible of rowbound visibility
       * @private
       */
      _hideOnMenuClose: function () {
        if (this._onMenuCloseHandler) {
          this._onMenuCloseHandler();
          this._onMenuCloseHandler = null;
        }
        this.setStyle({
          "--rowBoundVisibility": null
        });
        this.setAriaAttribute("expanded", false);
      }
    };
  });
  cls.WidgetFactory.registerBuilder("RowBoundDecorator", cls.RowBoundDecoratorWidget);
});
